/**
 * Süper Admin → Veri Migrasyonu
 * Preview mongodump'ını yükleyip fucan-001 verisini güvenli şekilde bu ortamın
 * MongoDB'sine (production'da prod cluster) restore eder.
 *
 * Akış:
 *   1. Backup .tar.gz yükle
 *   2. Dry Run → preflight + manifest önizleme
 *   3. Gerçek Migration → pre-backup + insert + parity + integrity + report
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ShieldAlert, Upload, PlayCircle, Rocket, RefreshCw, CheckCircle2,
  XCircle, AlertTriangle, Loader2, Database,
} from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const FOCUS_LABELS = {
  customers: "Müşteri",
  products: "Ürün",
  product_variants: "Ürün Varyantı",
  sales_orders: "Satış Siparişi",
  payments: "Tahsilat",
  employees: "Personel",
  suppliers: "Tedarikçi",
  warehouses: "Depo",
  stock: "Stok",
  users: "Kullanıcı",
  saas_companies: "Şirket Kaydı",
};

export default function DataMigration() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(null); // "upload" | "dry" | "exec" | "reset"
  const [confirmExec, setConfirmExec] = useState(false);

  useEffect(() => {
    if (user && !user.is_super_admin) navigate("/");
  }, [user, navigate]);

  const loadStatus = async () => {
    try {
      const r = await api.get("/super-admin/migration/status");
      setState(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Durum yüklenemedi");
    }
  };
  useEffect(() => { loadStatus(); }, []);

  const handleUpload = async (evt) => {
    const file = evt.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".tar.gz") && !file.name.toLowerCase().endsWith(".tgz")) {
      toast.error("Sadece .tar.gz dosyaları kabul edilir");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setBusy("upload");
    try {
      const r = await api.post("/super-admin/migration/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setState(r.data);
      toast.success(`Yüklendi — ${r.data.collections_found} koleksiyon bulundu`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Yükleme başarısız");
    } finally {
      setBusy(null);
      evt.target.value = "";
    }
  };

  const handleDryRun = async () => {
    setBusy("dry");
    try {
      const r = await api.post("/super-admin/migration/dry-run");
      setState(r.data);
      const rep = r.data.dry_run_report;
      if (r.data.dry_run_status === "passed") {
        toast.success(`Dry Run başarılı — ${rep?.plan?.total_source ?? 0} doküman aktarılabilir`);
      } else {
        toast.error("Dry Run DURDU — production'da fucan-001 verisi bulundu");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Dry Run başarısız");
    } finally {
      setBusy(null);
    }
  };

  const handleExecute = async () => {
    setBusy("exec");
    setConfirmExec(false);
    try {
      const r = await api.post("/super-admin/migration/execute");
      setState(r.data);
      if (r.data.execute_status === "success") {
        toast.success("Migration BAŞARILI — parity ve integrity kontrolleri geçti");
      } else if (r.data.execute_status === "completed_with_issues") {
        toast.warning("Migration tamamlandı ama parity sorunları var — raporu inceleyin");
      } else {
        toast.error("Migration DURDU — raporu inceleyin");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Migration başarısız");
    } finally {
      setBusy(null);
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Bu oturumu sıfırlamak istediğinizden emin misiniz? Yüklenen backup silinecek.")) return;
    setBusy("reset");
    try {
      const r = await api.post("/super-admin/migration/reset");
      setState(r.data);
      toast.success("Oturum sıfırlandı");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sıfırlama başarısız");
    } finally {
      setBusy(null);
    }
  };

  const dr = state?.dry_run_report;
  const ex = state?.execute_report;

  return (
    <div className="space-y-6" data-testid="data-migration-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-100 tracking-tight flex items-center gap-3">
            <Database className="w-7 h-7 text-blue-500" />
            Veri Migrasyonu (fucan-001)
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            Preview yedeğini bu ortamın veritabanına güvenli şekilde restore eder.
            Yalnızca <span className="text-blue-400 font-mono">fucan-001</span> tenant'ına ait kayıtlar aktarılır.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleReset}
          disabled={busy !== null || (!state?.session_id && !state?.executed_at)}
          data-testid="migration-reset-btn"
          className="rounded-sm text-xs uppercase tracking-wider"
        >
          <RefreshCw className="w-4 h-4 mr-2" /> Sıfırla
        </Button>
      </div>

      {/* Step 1: Upload */}
      <StepCard
        number={1}
        title="Backup Dosyasını Yükle"
        active={!state?.session_id}
        done={!!state?.session_id}
      >
        <p className="text-sm text-slate-400">
          Preview ortamında hazırlanmış <span className="font-mono">.tar.gz</span> mongodump arşivini yükleyin.
          Maksimum 200 MB. Dosya sunucuya kaydedilmez, sadece BSON içeriği geçici olarak çıkarılır.
        </p>
        <div className="flex items-center gap-3">
          <label className="cursor-pointer">
            <input
              type="file"
              accept=".tar.gz,.tgz,application/gzip,application/x-gzip"
              className="hidden"
              onChange={handleUpload}
              disabled={busy !== null || state?.executed_at}
              data-testid="migration-upload-input"
            />
            <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-sm text-xs uppercase tracking-wider font-semibold border transition-colors ${
              busy || state?.executed_at
                ? "bg-slate-800 border-slate-700 text-slate-500 cursor-not-allowed"
                : "bg-blue-600/20 border-blue-700 text-blue-300 hover:bg-blue-600/30"
            }`}>
              {busy === "upload" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              Dosya Seç
            </span>
          </label>
          {state?.session_id && (
            <div className="text-xs text-slate-400">
              Oturum: <span className="font-mono text-slate-300">{state.session_id.slice(0, 8)}</span> ·
              <span className="ml-2 text-emerald-400">{state.collections_found} koleksiyon</span>
              {state.uploaded_at && (
                <span className="ml-2">· {new Date(state.uploaded_at).toLocaleString("tr-TR")}</span>
              )}
            </div>
          )}
        </div>
      </StepCard>

      {/* Step 2: Dry Run */}
      <StepCard
        number={2}
        title="Dry Run (Yazma Yok)"
        active={state?.can_dry_run && !state?.dry_run_status}
        done={state?.dry_run_status === "passed"}
        failed={state?.dry_run_status === "failed"}
      >
        <p className="text-sm text-slate-400">
          Yükleme sonrası zorunludur. Yalnızca kaç kaydın taşınacağını hesaplar ve production'da
          çakışma olup olmadığını kontrol eder. Kesinlikle veri yazmaz.
        </p>
        <Button
          onClick={handleDryRun}
          disabled={!state?.can_dry_run || busy !== null}
          data-testid="migration-dry-run-btn"
          className="bg-slate-700 hover:bg-slate-600 rounded-sm uppercase tracking-wider text-xs font-semibold"
        >
          {busy === "dry" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PlayCircle className="w-4 h-4 mr-2" />}
          Dry Run Çalıştır
        </Button>

        {dr && <DryRunReport report={dr} />}
      </StepCard>

      {/* Step 3: Execute */}
      <StepCard
        number={3}
        title="Gerçek Migration"
        active={state?.can_execute}
        done={state?.execute_status === "success"}
        failed={state?.execute_status === "failed" || state?.execute_status === "completed_with_issues"}
      >
        <div className="rounded-sm border border-amber-800/60 bg-amber-950/30 p-3 flex gap-2 text-xs text-amber-200">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div>
            Bu buton yalnızca Dry Run başarılı olduğunda aktifleşir. Gerçek migration:
            (1) production'ın tam JSON yedeğini alır, (2) sadece fucan-001 kayıtlarını insert eder,
            (3) sayı + referans kontrolü yapar, (4) rapor üretir.
          </div>
        </div>
        <Button
          onClick={() => setConfirmExec(true)}
          disabled={!state?.can_execute || busy !== null}
          data-testid="migration-execute-btn"
          className="bg-blue-600 hover:bg-blue-500 rounded-sm uppercase tracking-wider text-xs font-semibold"
        >
          {busy === "exec" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Rocket className="w-4 h-4 mr-2" />}
          Gerçek Migration'ı Başlat
        </Button>

        {ex && <ExecuteReport report={ex} />}
      </StepCard>

      <AlertDialog open={confirmExec} onOpenChange={setConfirmExec}>
        <AlertDialogContent className="bg-slate-900 border-slate-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-slate-100">
              Migration'ı gerçekten başlatalım mı?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              Bu işlem fucan-001 kayıtlarını <span className="font-mono text-blue-300">
              {ex ? "" : (dr?.target_db || "bu ortamın veritabanına")}</span> yazar.
              Öncesinde tam yedek alınır ama işlem başlarsa geri döndürülmesi için manuel restore gerekir.
              <div className="mt-3 text-slate-300">
                Aktarılacak toplam: <span className="font-mono text-blue-300">{dr?.plan?.total_source ?? 0}</span> doküman.
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">İptal</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleExecute}
              className="bg-blue-600 hover:bg-blue-500 rounded-sm"
              data-testid="migration-execute-confirm"
            >
              Evet, Başlat
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function StepCard({ number, title, active, done, failed, children }) {
  const stateColor = done
    ? "border-emerald-800/60"
    : failed
    ? "border-rose-800/60"
    : active
    ? "border-blue-800/60"
    : "border-slate-800";
  const numColor = done
    ? "bg-emerald-600 text-white"
    : failed
    ? "bg-rose-600 text-white"
    : active
    ? "bg-blue-600 text-white"
    : "bg-slate-800 text-slate-400";

  return (
    <Card className={`bg-slate-900/50 border ${stateColor} rounded-sm`} data-testid={`migration-step-${number}`}>
      <CardHeader className="border-b border-slate-800 py-3 flex-row items-center gap-3">
        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${numColor}`}>
          {done ? <CheckCircle2 className="w-4 h-4" /> : failed ? <XCircle className="w-4 h-4" /> : number}
        </div>
        <CardTitle className="text-slate-100 uppercase text-xs tracking-widest font-semibold flex-1">
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4 space-y-3">{children}</CardContent>
    </Card>
  );
}

function DryRunReport({ report }) {
  const preflight = report?.preflight;
  const focus = report?.focus_counts || {};
  const total = report?.plan?.total_source ?? 0;

  return (
    <div className="mt-4 space-y-3" data-testid="dry-run-report">
      {preflight && !preflight.clean ? (
        <div className="rounded-sm border border-rose-800/60 bg-rose-950/30 p-3 text-xs text-rose-200">
          <div className="font-semibold mb-1">Preflight DURDU</div>
          <div>Production'da halihazırda fucan-001 verisi var. Bu koleksiyonlarda:</div>
          <ul className="list-disc list-inside mt-1 text-rose-300 font-mono">
            {preflight.issues.slice(0, 10).map((i, idx) => <li key={idx}>{i}</li>)}
            {preflight.issues.length > 10 && <li>… +{preflight.issues.length - 10} daha</li>}
          </ul>
        </div>
      ) : (
        <div className="rounded-sm border border-emerald-800/60 bg-emerald-950/30 p-3 text-xs text-emerald-200 flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4" />
          Preflight temiz — hedef veritabanında fucan-001 kaydı yok. Toplam <span className="font-mono font-bold">{total}</span> doküman aktarılmaya hazır.
        </div>
      )}

      {Object.keys(focus).length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Kritik Kayıt Sayıları</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {Object.entries(focus).map(([k, v]) => (
              <div key={k} className="rounded-sm border border-slate-800 bg-slate-900/50 p-2">
                <div className="text-[10px] uppercase tracking-widest text-slate-500">{FOCUS_LABELS[k] || k}</div>
                <div className="text-lg font-bold text-slate-100 font-mono">{v}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <details className="text-xs">
        <summary className="cursor-pointer text-slate-400 hover:text-slate-200">Tüm koleksiyonların detayı ({report?.collections_found})</summary>
        <div className="mt-2 max-h-64 overflow-auto rounded-sm border border-slate-800">
          <Table>
            <TableHeader>
              <TableRow className="border-slate-800">
                <TableHead className="text-slate-400 text-xs">Koleksiyon</TableHead>
                <TableHead className="text-slate-400 text-xs text-right">Kaynak</TableHead>
                <TableHead className="text-slate-400 text-xs">Not</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Object.entries(report?.plan?.per_collection || {})
                .sort(([, a], [, b]) => (b.source || 0) - (a.source || 0))
                .map(([coll, info]) => (
                  <TableRow key={coll} className="border-slate-800">
                    <TableCell className="text-slate-300 font-mono text-xs">{coll}</TableCell>
                    <TableCell className="text-right text-slate-100 font-mono">{info.source || 0}</TableCell>
                    <TableCell className="text-slate-500 text-xs">
                      {info.skipped || info.error || (info.source === 0 && "—")}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>
      </details>
    </div>
  );
}

function ExecuteReport({ report }) {
  const parity = report?.parity;
  const integrity = report?.integrity;
  const focus = report?.focus_counts_after || {};

  return (
    <div className="mt-4 space-y-3" data-testid="execute-report">
      {report.status === "SUCCESS" ? (
        <div className="rounded-sm border border-emerald-800/60 bg-emerald-950/30 p-3 text-xs text-emerald-200 flex items-center gap-2">
          <CheckCircle2 className="w-5 h-5" />
          <div>
            <div className="font-semibold">MIGRATION BAŞARILI</div>
            <div>Tüm parity kontrolleri geçti. Tamamlandı: {new Date(report.finished_at).toLocaleString("tr-TR")}</div>
          </div>
        </div>
      ) : report.status === "HALTED" || report.status === "HALTED_MID_RESTORE" ? (
        <div className="rounded-sm border border-rose-800/60 bg-rose-950/30 p-3 text-xs text-rose-200">
          <div className="font-semibold flex items-center gap-2"><XCircle className="w-4 h-4" /> MIGRATION DURDU</div>
          <div className="mt-1">{report.halt_reason}</div>
        </div>
      ) : (
        <div className="rounded-sm border border-amber-800/60 bg-amber-950/30 p-3 text-xs text-amber-200 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" />
          Tamamlandı ama parity sorunlu — aşağıdaki detayı inceleyin.
        </div>
      )}

      {Object.keys(focus).length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Restore Sonrası Sayımlar</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {Object.entries(focus).map(([k, v]) => (
              <div key={k} className="rounded-sm border border-slate-800 bg-slate-900/50 p-2">
                <div className="text-[10px] uppercase tracking-widest text-slate-500">{FOCUS_LABELS[k] || k}</div>
                <div className="text-lg font-bold text-emerald-300 font-mono">{v}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {parity && (
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Parity</div>
          {parity.ok ? (
            <Badge className="bg-emerald-950/60 text-emerald-300 border-emerald-800 rounded-sm">Tüm sayımlar eşleşti</Badge>
          ) : (
            <div className="rounded-sm border border-rose-800/60 bg-rose-950/30 p-2 text-xs text-rose-200 font-mono">
              {parity.mismatches.map((m, i) => (
                <div key={i}>{m.collection}: kaynak={m.source} / hedef={m.target}</div>
              ))}
            </div>
          )}
        </div>
      )}

      {integrity && (
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Referans Bütünlüğü</div>
          {integrity.ok ? (
            <Badge className="bg-emerald-950/60 text-emerald-300 border-emerald-800 rounded-sm">Orphan referans yok</Badge>
          ) : (
            <details className="text-xs">
              <summary className="cursor-pointer text-amber-300">
                {integrity.problems.length} adet orphan grup bulundu (kaynak veride zaten mevcut olabilir)
              </summary>
              <div className="mt-2 space-y-1 font-mono">
                {integrity.problems.map((p, i) => (
                  <div key={i} className="text-slate-300">
                    {p.child} → {p.parent}: <span className="text-amber-300">{p.orphan_count}</span> orphan
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}

      {report?.pre_backup?.path && (
        <div className="text-xs text-slate-500">
          Pre-migration yedek: <span className="font-mono">{report.pre_backup.path}</span> · <span>{report.pre_backup.total_docs} doküman</span>
        </div>
      )}
    </div>
  );
}
